import { getInventoryRows, applyMovement } from "./inventory";
import type { MovementType, WarehouseCode } from "../types";

interface ModelContextLike {
  registerTool(tool: Record<string, unknown>, options?: { signal?: AbortSignal }): void | Promise<void>;
}

declare global {
  interface Document { modelContext?: ModelContextLike }
}

export function registerInventoryTools() {
  const context = document.modelContext;
  if (!context?.registerTool) return () => undefined;
  const lifecycle = new AbortController();
  const report = () => undefined;

  void Promise.resolve(context.registerTool({
    name: "search_inventory",
    title: "Pesquisar estoque",
    description: "Pesquisa o estoque da WG Tricot por SKU, produto ou cor e retorna os saldos locais atuais.",
    inputSchema: { type: "object", properties: { query: { type: "string" } }, required: ["query"], additionalProperties: false },
    annotations: { readOnlyHint: true, untrustedContentHint: false },
    execute: async (input: unknown) => {
      const query = String((input as { query?: unknown })?.query ?? "").trim().toLowerCase();
      if (!query) throw new Error("Informe SKU, produto ou cor.");
      const rows = (await getInventoryRows()).filter((row) => `${row.product.sku} ${row.product.name} ${row.variant.colorName}`.toLowerCase().includes(query));
      return rows.slice(0, 20).map((row) => ({ sku: row.product.sku, product: row.product.name, color: row.variant.colorName, galpao: row.galpao.quantity, malharia: row.variant.malhariaEnabled ? row.malharia?.quantity ?? 0 : null, total: row.total }));
    },
  }, { signal: lifecycle.signal })).catch(report);

  void Promise.resolve(context.registerTool({
    name: "create_inventory_movement",
    title: "Registrar movimentação de estoque",
    description: "Registra uma entrada ou saída relativa no estoque local e na fila de sincronização.",
    inputSchema: {
      type: "object",
      properties: {
        variantId: { type: "string" }, warehouse: { type: "string", enum: ["galpao", "malharia"] },
        movementType: { type: "string", enum: ["entrada", "venda", "ajuste_positivo", "ajuste_negativo", "devolucao", "correcao"] },
        quantity: { type: "integer" }, note: { type: "string" },
      },
      required: ["variantId", "warehouse", "movementType", "quantity"], additionalProperties: false,
    },
    annotations: { readOnlyHint: false, untrustedContentHint: false },
    execute: async (input: unknown) => {
      const value = input as { variantId?: string; warehouse?: WarehouseCode; movementType?: MovementType; quantity?: number; note?: string };
      if (!value.variantId || !value.warehouse || !value.movementType || !Number.isInteger(value.quantity) || value.quantity === 0) throw new Error("Movimentação inválida.");
      const movement = await applyMovement({ variantId: value.variantId, warehouse: value.warehouse, type: value.movementType, quantity: value.quantity, note: value.note });
      return { operationId: movement.operationId, before: movement.before, after: movement.after, syncStatus: movement.syncStatus };
    },
  }, { signal: lifecycle.signal })).catch(report);

  return () => lifecycle.abort();
}
